import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  inject,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { CommonModule } from '@angular/common';

import { DropdownToggleHelper } from '../../helpers/dropdown-toggle.helper';

export type MultiSelectAppearance = 'default' | 'form';

export interface MultiSelectOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-wi-multi-select',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './wi-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./wi-multi-select.component.scss'],
})
export class MultiSelectComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() options: MultiSelectOption[] = [];

  @Input() selectedIds: string[] = [];

  @Input() placeholder = 'Select...';

  /** Matches modal / form field styling when set to `form`. */
  @Input() appearance: MultiSelectAppearance = 'default';

  /** Shows validation error border (form appearance). */
  @Input() error = false;

  /** When false, hides the "All" option. */
  @Input() showSelectAll = true;

  /** When false, hides selected chips below the trigger. */
  @Input() showTags = true;

  @Input() tagsTitle = 'Selected';

  /**
   * Label when multiple items are selected. Use `{{count}}` as a placeholder.
   * @example "{{count}} selected"
   */
  @Input() multipleSelectionText = '{{count}} selected';

  /** Shown when one item is selected but its label cannot be resolved. */
  @Input() singleFallbackText = '1 selected';

  /** Shows a search field at the top of the dropdown panel. */
  @Input() searchable = false;

  @Input() searchPlaceholder = 'Search...';

  @Output()
  selectedIdsChange = new EventEmitter<string[]>();

  readonly dropdownId = `multi-${Math.random().toString(36).slice(2)}`;

  @ViewChild('panelRef') panelRef?: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  panelStyles: Record<string, string> = {};
  searchQuery = '';

  get open(): boolean {
    return DropdownToggleHelper.isOpen(this.dropdownId);
  }

  get filteredOptions(): MultiSelectOption[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.options;
    }

    return this.options.filter(option => option.name.toLowerCase().includes(query));
  }

  get allSelected(): boolean {
    const visible = this.filteredOptions;
    return visible.length > 0 && visible.every(option => this.isSelected(option.id));
  }

  get selectedOptions(): MultiSelectOption[] {
    const set = new Set(this.selectedIds);

    return this.options.filter(o => set.has(o.id));
  }

  get triggerText(): string {
    if (this.selectedIds.length === 0) {
      return this.placeholder;
    }

    if (this.selectedIds.length === 1) {
      return this.selectedOptions[0]?.name ?? this.singleFallbackText;
    }

    return this.multipleSelectionText.replace('{{count}}', String(this.selectedIds.length));
  }

  toggleOpen(): void {
    const opened = DropdownToggleHelper.toggle(this.dropdownId);

    if (!opened) {
      this.searchQuery = '';
      return;
    }

    this.panelStyles = {};
    this.searchQuery = '';

    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.updatePanelPosition();
      if (this.searchable) {
        this.searchInput?.nativeElement?.focus();
      }
    });
  }

  close(): void {
    DropdownToggleHelper.close(this.dropdownId);
    this.searchQuery = '';
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchQuery = target?.value ?? '';
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.updatePanelPosition();
    });
  }

  onSearchKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  isSelected(id: string): boolean {
    return this.selectedIds.includes(id);
  }

  toggleOne(id: string): void {
    if (this.isSelected(id)) {
      this.selectedIdsChange.emit(this.selectedIds.filter(x => x !== id));
    } else {
      this.selectedIdsChange.emit([...this.selectedIds, id]);
    }
  }

  toggleAll(): void {
    const visibleIds = this.filteredOptions.map(option => option.id);

    if (this.allSelected) {
      const visibleSet = new Set(visibleIds);
      this.selectedIdsChange.emit(this.selectedIds.filter(id => !visibleSet.has(id)));
      return;
    }

    this.selectedIdsChange.emit([...new Set([...this.selectedIds, ...visibleIds])]);
  }

  remove(id: string): void {
    this.selectedIdsChange.emit(this.selectedIds.filter(x => x !== id));
  }

  updatePanelPosition(secondPass = false): void {
    if (!this.panelRef) {
      return;
    }

    const trigger = this.host.nativeElement.querySelector('.trigger') as HTMLElement;

    const panel = this.panelRef.nativeElement;

    if (!trigger || !panel) {
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const gap = 8;
    let left = triggerRect.left;
    let top = triggerRect.bottom + gap;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (left + panelRect.width > vw - 12) {
      left = vw - panelRect.width - 12;
    }

    if (top + panelRect.height > vh - 12) {
      top = triggerRect.top - panelRect.height - gap;
    }

    if (top < 12) {
      top = 12;
    }

    const styles: Record<string, string> = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      zIndex: '3000',
    };

    if (this.appearance === 'form') {
      styles['width'] = `${triggerRect.width}px`;
      styles['minWidth'] = `${triggerRect.width}px`;
      styles['maxWidth'] = `${triggerRect.width}px`;
    }

    this.panelStyles = styles;
    this.cdr.detectChanges();

    if (this.appearance === 'form' && !secondPass) {
      requestAnimationFrame(() => {
        this.updatePanelPosition(true);
      });
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onOutsideClick(event: MouseEvent) {
    const target = event.target as Node;

    const clickedInside = this.host.nativeElement.contains(target);

    if (!clickedInside) {
      this.close();
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.open) {
      requestAnimationFrame(() => {
        this.updatePanelPosition();
      });
    }
  }

  @HostListener('window:scroll')
  onScroll() {
    if (this.open) {
      requestAnimationFrame(() => {
        this.updatePanelPosition();
      });
    }
  }
}
